import { Controller } from '@hotwired/stimulus'
import { Editor } from '@tiptap/core'
import Color from '@tiptap/extension-color'
import Link from '@tiptap/extension-link'
import { TextStyle } from '@tiptap/extension-text-style'
import Underline from '@tiptap/extension-underline'
import StarterKit from '@tiptap/starter-kit'
import TurndownService from 'turndown'

const colorTokens = {
  '#2563eb': 'blue',
  '#dc2626': 'red',
  'rgb(37, 99, 235)': 'blue',
  'rgb(220, 38, 38)': 'red'
}

export default class extends Controller {
  static targets = ['editor', 'input', 'status']
  static values = { autosaveMethod: String, autosaveUrl: String, content: String }

  connect () {
    this.turndown = new TurndownService()
    this.configureTurndown()

    this.editor = new Editor({
      element: this.editorTarget,
      extensions: [
        StarterKit,
        TextStyle,
        Color,
        Link.configure({ openOnClick: false }),
        Underline
      ],
      content: this.contentValue || '<p></p>',
      editorProps: {
        attributes: {
          class: 'journal-editor-content focus:outline-none'
        }
      },
      onUpdate: ({ editor }) => {
        this.inputTarget.value = this.htmlToMarkdown(editor.getHTML())
        this.queueAutosave()
      }
    })

    this.inputTarget.value = this.htmlToMarkdown(this.editor.getHTML())
    if (this.hasAutosaveUrlValue && this.autosaveUrlValue !== '') this.setStatus('draft')
    this.element.addEventListener('submit', this.beforeSubmit)
  }

  disconnect () {
    clearTimeout(this.autosaveTimeout)
    this.element.removeEventListener('submit', this.beforeSubmit)
    this.editor?.destroy()
  }

  beforeSubmit = () => {
    clearTimeout(this.autosaveTimeout)
    this.inputTarget.value = this.htmlToMarkdown(this.editor.getHTML())
  }

  configureTurndown () {
    this.turndown.addRule('journalColor', {
      filter: (node) => {
        return node.nodeName === 'SPAN' && this.colorTokenFor(node.style?.color)
      },
      replacement: (content, node) => {
        const token = this.colorTokenFor(node.style.color)
        return `[${token}]${content}[/${token}]`
      }
    })

    this.turndown.addRule('journalUnderline', {
      filter: ['u'],
      replacement: (content) => `++${content}++`
    })
  }

  colorTokenFor (color) {
    return colorTokens[color?.toLowerCase()]
  }

  csrfToken () {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  htmlToMarkdown (html) {
    return this.turndown.turndown(html).trim()
  }

  queueAutosave () {
    if (!this.hasAutosaveUrlValue || this.autosaveUrlValue === '') return

    this.setStatus('draft')
    clearTimeout(this.autosaveTimeout)
    this.autosaveTimeout = setTimeout(() => this.save(), 1000)
  }

  async save () {
    this.setStatus('saving')

    const body = new FormData()
    body.append('autosave', '1')
    body.append('spiritual_practice_entry[practice_key]', 'journal')
    body.append('spiritual_practice_entry[payload][content]', this.inputTarget.value)

    try {
      const response = await fetch(this.autosaveUrlValue, {
        method: this.autosaveMethodValue || 'PATCH',
        headers: {
          Accept: 'application/json, text/plain',
          'X-CSRF-Token': this.csrfToken()
        },
        body
      })

      if (response.ok && (this.autosaveMethodValue || 'PATCH') === 'POST') {
        const data = await response.json()
        this.autosaveMethodValue = 'PATCH'
        this.autosaveUrlValue = data.autosave_url
        this.element.dispatchEvent(new CustomEvent('time-tracker:record-id', {
          detail: { recordId: data.id }
        }))
      }

      this.setStatus(response.ok ? 'saved' : 'save failed')
    } catch (_error) {
      this.setStatus('save failed')
    }
  }

  setStatus (status) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = status
  }

  setBlack () {
    this.editor.chain().focus().unsetColor().run()
  }

  setBlue () {
    this.editor.chain().focus().setColor('#2563eb').run()
  }

  toggleBold () {
    this.editor.chain().focus().toggleBold().run()
  }

  toggleBulletList () {
    this.editor.chain().focus().toggleBulletList().run()
  }

  setLink () {
    const previousUrl = this.editor.getAttributes('link').href
    const url = window.prompt('Link URL', previousUrl || 'https://')

    if (url === null) return

    if (url === '') {
      this.editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }

    this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  setRed () {
    this.editor.chain().focus().setColor('#dc2626').run()
  }

  toggleHeading1 () {
    this.editor.chain().focus().toggleHeading({ level: 1 }).run()
  }

  toggleHeading2 () {
    this.editor.chain().focus().toggleHeading({ level: 2 }).run()
  }

  toggleHeading3 () {
    this.editor.chain().focus().toggleHeading({ level: 3 }).run()
  }

  toggleItalic () {
    this.editor.chain().focus().toggleItalic().run()
  }

  toggleOrderedList () {
    this.editor.chain().focus().toggleOrderedList().run()
  }

  toggleUnderline () {
    this.editor.chain().focus().toggleUnderline().run()
  }
}
